import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { LevelSummary } from '@/features/guardian-player/components/player-progress-tracker/types';
import { BottomSideBorder } from '@/features/guardian/components/bottom-side-border';
import { useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import React, { memo, useCallback, useState } from 'react';
import { Pressable, type LayoutChangeEvent } from 'react-native';

import { BadgeProgressDots } from './badge-progress-dots';
import { LevelSeal } from './level-seal';
import { useStyles } from './styles';

const strings = STRINGS.guardian.playerProgressTracker;
const LEVEL_BORDER_EDGE_WIDTH = 4;
const LEVEL_BORDER_TIP_WIDTH = 0;
const LEVEL_BORDER_STEPS = 24;

type LevelCardProps = {
  item: LevelSummary;
  onPress?: (item: LevelSummary) => void;
};

function LevelCard({ item, onPress }: LevelCardProps) {
  const styles = useStyles();
  const { scale } = useScaling();
  const textColor = item.isLocked ? guardianColors['5'] : item.color;
  const statusColor = item.isEarned ? colors.Success : guardianColors['Primary Color'];
  const borderColor = item.isLocked ? guardianColors['5'] : item.color;
  const starColor = item.isLocked ? guardianColors['5'] : guardianColors['12'];
  const isPressable = Boolean(onPress) && !item.isLocked;
  const [measuredSize, setMeasuredSize] = useState({ width: 0, height: 0 });

  const handlePress = useCallback(() => {
    onPress?.(item);
  }, [item, onPress]);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setMeasuredSize(previousSize =>
      previousSize.width === width && previousSize.height === height
        ? previousSize
        : { width, height },
    );
  }, []);

  const renderStatusContent = () => {
    if (item.isEarned) {
      return (
        <>
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
            numberOfLines={1}
            style={{ color: statusColor }}>
            {strings.earned}
          </Text>
          {item.completedOn ? (
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 11 Regular"
              numberOfLines={1}
              style={styles.mutedText}>
              {strings.earnedDate.replace('{date}', item.completedOn)}
            </Text>
          ) : null}
        </>
      );
    }
    if (item.isLocked) {
      return (
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 14 Italic"
          fontStyle="italic"
          numberOfLines={1}
          style={styles.unlockText}>
          {strings.unlockAfterLevel.replace('{level}', String(item.level - 1))}
        </Text>
      );
    }
    return (
      <>
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
          numberOfLines={1}
          style={{ color: statusColor }}>
          {strings.inProgress}
        </Text>
        <BadgeProgressDots progress={item.progress} color={colors.Blue} />
      </>
    );
  };

  return (
    <Pressable
      accessibilityRole={isPressable ? 'button' : undefined}
      accessibilityLabel={strings.levelAccessibilityLabel.replace('{level}', String(item.level))}
      accessibilityState={{ disabled: !isPressable }}
      disabled={!isPressable}
      onPress={handlePress}
      style={({ pressed }) => [
        styles.levelCardPressable,
        pressed && isPressable ? styles.pressed : null,
      ]}>
      <Stack onLayout={handleLayout} style={styles.levelCard}>
        <Stack
          direction="row"
          viewType={item.isLocked ? 'liquid-glass' : 'view'}
          align="center"
          gap={scale(8)}
          style={[styles.levelCardContent, item.isLocked ? styles.lockedLevelCard : null]}>
          <LevelSeal item={item} />
          <Stack flex={1} gap={scale(8)}>
            <Text variant="Font Guardian App/Body/Body 16 Bold" style={{ color: textColor }}>
              {strings.levelTitle.replace('{level}', String(item.level))}
            </Text>
            <Stack direction="row" align="center" gap={scale(6)}>
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                style={[styles.badgeCountText, item.isLocked ? styles.lockedBadgeCountText : null]}>
                {strings.badgeCount
                  .replace('{earned}', String(item.earnedCount))
                  .replace('{total}', String(item.totalCount))}
              </Text>
              <SvgIcon
                name={item.isEarned ? 'IcStar' : 'IcStarOutline'}
                size={scale(16)}
                color={starColor}
              />
            </Stack>
          </Stack>

          <Stack align="flex-end" gap={scale(10)} style={styles.levelStatus}>
            {renderStatusContent()}
          </Stack>
        </Stack>

        <BottomSideBorder
          color={borderColor}
          edgeWidth={LEVEL_BORDER_EDGE_WIDTH}
          height={measuredSize.height}
          radius={scale(12)}
          side="left"
          steps={LEVEL_BORDER_STEPS}
          tipWidth={LEVEL_BORDER_TIP_WIDTH}
          width={measuredSize.width}
        />
      </Stack>
    </Pressable>
  );
}

export default memo(LevelCard);
